/**
 * PÁGINA DE CONTACTO COMPLETA
 * ===========================
 * Información de contacto, formulario, mapa y redes sociales
 */

import React, { useState, useEffect } from 'react';
import {
  MapPin, Phone, Mail, Clock, Send, Facebook,
  Twitter, Instagram, Linkedin, CheckCircle
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import ModernHeader from '../components/modern/ModernHeader';
import ModernFooter from '../components/modern/ModernFooter';

// Fix leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const ContactoCompletaPage = () => {
  const [scrollY, setScrollY] = useState(0);
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    asunto: '',
    mensaje: ''
  });
  const [enviado, setEnviado] = useState(false);
  const [enviando, setEnviando] = useState(false);

  // Manejar scroll para header
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Manejar cambios en formulario
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Enviar formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    setEnviando(true);

    // TODO: Aquí iría la llamada a la API para enviar el formulario
    // await api.enviarContacto(formData);

    // Simular envío
    setTimeout(() => {
      setEnviando(false);
      setEnviado(true);

      // Resetear formulario
      setFormData({
        nombre: '',
        email: '',
        telefono: '',
        asunto: '',
        mensaje: ''
      });

      // Ocultar mensaje de éxito después de 5 segundos
      setTimeout(() => setEnviado(false), 5000);
    }, 1500);
  };

  // Información de contacto
  const infoContacto = {
    direccion: 'Av Juan M Ferrari 1251, Oficina 11, Montevideo, Uruguay',
    telefonoPrincipal: '26041258',
    email: 'info@plazarural.com',
    horarioAtencion: 'Lunes a Viernes: 9:00 - 18:00',
    coordenadas: {
      lat: -34.8935473009195,
      lng: -56.05953944232938
    }
  };

  // Redes sociales
  const redesSociales = [
    { nombre: 'Facebook', icono: Facebook, url: 'https://www.facebook.com/@www.plazarural.com.uy/', color: 'bg-blue-600 hover:bg-blue-700' },
    { nombre: 'Instagram', icono: Instagram, url: 'https://www.instagram.com/plazarural', color: 'bg-pink-600 hover:bg-pink-700' },
    { nombre: 'Twitter', icono: Twitter, url: 'https://x.com/plazarural', color: 'bg-black hover:bg-gray-800' },
    { nombre: 'LinkedIn', icono: Linkedin, url: 'https://uy.linkedin.com/company/plaza-rural', color: 'bg-blue-700 hover:bg-blue-800' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-green-50">
      <ModernHeader scrollY={scrollY} alwaysOpaque={true} />

      <div className="pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4">

          {/* Header de la página */}
          <div className="mb-8 text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              Contacto
            </h1>
            <p className="text-gray-600 text-lg">
              Estamos aquí para ayudarte. Comunícate con nosotros por cualquiera de estos medios
            </p>
          </div>

          {/* Grid principal */}
          <div className="grid lg:grid-cols-3 gap-8 mb-8">

            {/* Columna izquierda - Información de contacto */}
            <div className="lg:col-span-1">

              {/* Información principal */}
              <div className="bg-gradient-to-br from-green-600 to-green-500 rounded-2xl p-6 text-white shadow-xl h-full flex flex-col justify-between">
                <div>
                  <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
                    <Phone className="w-6 h-6" />
                    Información de Contacto
                  </h2>

                  <div className="space-y-5">
                    {/* Dirección */}
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold mb-1">Dirección</p>
                        <p className="text-green-50 text-sm">{infoContacto.direccion}</p>
                      </div>
                    </div>

                    {/* Teléfono */}
                    <div className="flex items-start gap-3">
                      <Phone className="w-5 h-5 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold mb-1">Teléfono</p>
                        <p className="text-green-50 text-sm">{infoContacto.telefonoPrincipal}</p>
                      </div>
                    </div>

                    {/* Email */}
                    <div className="flex items-start gap-3">
                      <Mail className="w-5 h-5 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold mb-1">Email</p>
                        <p className="text-green-50 text-sm">{infoContacto.email}</p>
                      </div>
                    </div>

                    {/* Horarios */}
                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold mb-1">Horarios de Atención</p>
                        <p className="text-green-50 text-sm">{infoContacto.horarioAtencion}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Redes sociales */}
                <div className="mt-8 pt-6 border-t border-green-400">
                  <p className="font-semibold mb-3">Síguenos en redes sociales</p>
                  <div className="flex gap-3">
                    {redesSociales.map((red) => {
                      const IconoRed = red.icono;
                      return (
                        <a
                          key={red.nombre}
                          href={red.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-white/20 hover:bg-white/30 p-3 rounded-lg transition-colors"
                          title={red.nombre}
                        >
                          <IconoRed className="w-5 h-5" />
                        </a>
                      );
                    })}
                  </div>
                </div>
              </div>

            </div>

            {/* Columna derecha - Formulario */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-200 h-full">
                <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <Send className="w-6 h-6 text-green-600" />
                  Envíanos un Mensaje
                </h2>
                <p className="text-gray-600 mb-3 text-sm">
                  Completa el formulario y nos pondremos en contacto contigo a la brevedad
                </p>

                {/* Mensaje de éxito */}
                {enviado && (
                  <div className="mb-6 bg-green-50 border-2 border-green-500 rounded-xl p-4 flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                    <div>
                      <p className="font-bold text-green-900">¡Mensaje enviado con éxito!</p>
                      <p className="text-sm text-green-700">Nos pondremos en contacto contigo pronto.</p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Nombre */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      Nombre Completo *
                    </label>
                    <input
                      type="text"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-500 focus:outline-none transition-colors text-sm"
                      placeholder="Tu nombre completo"
                    />
                  </div>

                  {/* Email y Teléfono */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-500 focus:outline-none transition-colors text-sm"
                        placeholder="tu@email.com"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        Teléfono
                      </label>
                      <input
                        type="tel"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-500 focus:outline-none transition-colors text-sm"
                        placeholder="099 123 456"
                      />
                    </div>
                  </div>

                  {/* Asunto */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      Asunto *
                    </label>
                    <select
                      name="asunto"
                      value={formData.asunto}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-500 focus:outline-none transition-colors cursor-pointer bg-white text-sm"
                    >
                      <option value="">Selecciona un asunto</option>
                      <option value="consulta-general">Consulta General</option>
                      <option value="informacion-remate">Información sobre Remates</option>
                      <option value="compra-lotes">Compra de Lotes</option>
                      <option value="venta-ganado">Venta de Ganado</option>
                      <option value="soporte">Soporte Técnico</option>
                      <option value="prensa">Medios y Prensa</option>
                      <option value="otro">Otro</option>
                    </select>
                  </div>

                  {/* Mensaje */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      Mensaje *
                    </label>
                    <textarea
                      name="mensaje"
                      value={formData.mensaje}
                      onChange={handleChange}
                      required
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-500 focus:outline-none transition-colors resize-none text-sm"
                      placeholder="Escribe tu mensaje aquí..."
                    />
                  </div>

                  {/* Botón enviar */}
                  <button
                    type="submit"
                    disabled={enviando}
                    className={`w-full bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white font-bold py-3 rounded-lg transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center gap-2 text-sm ${enviando ? 'opacity-75 cursor-not-allowed' : ''
                      }`}
                  >
                    {enviando ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Enviar Mensaje
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* Mapa */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
            <div className="p-6 bg-gradient-to-r from-green-600 to-green-500">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <MapPin className="w-6 h-6" />
                Nuestra Ubicación
              </h2>
              <p className="text-green-50 mt-1">
                Encuéntranos en {infoContacto.direccion}
              </p>
            </div>

            {/* Mapa con Leaflet */}
            <div className="relative h-[500px] bg-gray-100 z-0">
              <MapContainer
                center={[infoContacto.coordenadas.lat, infoContacto.coordenadas.lng]}
                zoom={16}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={false}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker position={[infoContacto.coordenadas.lat, infoContacto.coordenadas.lng]}>
                  <Popup>
                    <div className="text-center">
                      <p className="font-bold text-green-700">Plaza Rural</p>
                      <p className="text-xs">{infoContacto.direccion}</p>
                    </div>
                  </Popup>
                </Marker>
              </MapContainer>

              {/* Overlay decorativo (opcional) */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-6 pointer-events-none z-[1000]">
                <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4 max-w-md pointer-events-auto">
                  <p className="font-bold text-gray-900 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-green-600" />
                    Plaza Rural
                  </p>
                  <p className="text-sm text-gray-600 mt-1">{infoContacto.direccion}</p>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${infoContacto.coordenadas.lat},${infoContacto.coordenadas.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-green-600 font-semibold hover:text-green-700 mt-2 inline-block"
                  >
                    Abrir en Google Maps →
                  </a>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <ModernFooter />
    </div>
  );
};

export default ContactoCompletaPage;
